import { ArticleSummary, listArticle } from 'stores/ArticleStoreAdapter';

/**
 * ArticleNav の articleId 補完（`self:` / `articles:`）で使う「自分が著者の記事一覧」の共有ストア。
 *
 * - KnzEditor が起動時に現在ユーザーの userId を setCurrentUserId でセットする。
 * - Monaco 補完（knzLanguage）が getArticleSuggestions() で候補一覧を取得する。
 *
 * link 補完（linkFileStore）と同じく、React ツリー外（Monaco 補完）から参照するため
 * モジュールレベルのミュータブルな参照にする。
 *
 * 取得方式は仕様 Q-5 の B 案（全件取得＋ローカル絞り込み）:
 * `listArticle({ grouping: true, authorUserIds: [自分] })` で自分の記事を全件取得してキャッシュし、
 * 補完側で入力文字をローカルフィルタする。ページングは持ち込まない。
 */

/** 補完候補に使う記事情報（articleId / タイトル / サブタイトル） */
export type ArticleSuggestion = {
  articleId: string;
  title: string;
  subtitle?: string;
};

/** 現在ログイン中ユーザーの userId。ゲスト（未ログイン）なら null。 */
let currentUserId: string | null = null;

/** 取得済みの候補キャッシュ（userId 単位で有効）。 */
let cache: { userId: string; suggestions: ArticleSuggestion[] } | null = null;

/** 取得中の Promise（重複リクエストをまとめる）。 */
let inflight: Promise<ArticleSuggestion[]> | null = null;

/**
 * 現在ユーザーの userId を設定する（KnzEditor が呼ぶ）。
 * userId が変わったらキャッシュを破棄する（別ユーザーの記事を出さないため）。
 */
export const setCurrentUserId = (userId: string | null): void => {
  if (currentUserId !== userId) {
    cache = null;
    inflight = null;
  }
  currentUserId = userId;
};

/** ArticleSummary を補完候補に変換する。 */
const toSuggestion = (a: ArticleSummary): ArticleSuggestion => ({
  articleId: a.articleId,
  title: a.title,
  subtitle: a.subtitle,
});

/**
 * 自分が著者の記事一覧を補完候補として取得する。
 * 取得済みならキャッシュを返す。userId 未設定（ゲスト）なら空配列。
 * 取得失敗時は空配列（補完なし。致命的ではない）。
 */
export const getArticleSuggestions = async (): Promise<ArticleSuggestion[]> => {
  const userId = currentUserId;
  if (!userId) return [];

  if (cache && cache.userId === userId) return cache.suggestions;
  if (inflight) return inflight;

  const p = listArticle({ grouping: true, authorUserIds: [userId] })
    .then((res) => {
      const suggestions = res.articles.map(toSuggestion);
      // userId が取得中に変わっていなければキャッシュする
      if (currentUserId === userId) cache = { userId, suggestions };
      return suggestions;
    })
    .catch(() => {
      // 取得失敗は補完なし（空配列）。キャッシュはしない（次回再取得を許す）。
      return [];
    })
    .finally(() => {
      inflight = null;
    });
  inflight = p;
  return p;
};

/** キャッシュをクリアする（記事の作成・削除後など、候補を作り直したいときに使う）。 */
export const clearArticleSuggestions = (): void => {
  cache = null;
  inflight = null;
};
